// useProgressSummary.ts
import { useCallback } from 'react'
import type { Task } from 'types'
import { SubtaskRelevantData } from './types'

const defaultCounts = {
  done: 0,
  inProgress: 0,
  onHold: 0,
  needsReview: 0,
  needMoreInfo: 0,
  notStarted: 0,
}

type Options = { task?: Task }

export const useProgressSummary = () => {
  return useCallback((subtasks: SubtaskRelevantData[] = [], opts: Options = {}) => {
    // === 1) Legacy path: derive from subtasks if any exist ===
    if (Array.isArray(subtasks) && subtasks.length > 0) {
      const statusCounts = subtasks.reduce(
        (acc, subtask) => {
          const status = (subtask as any).masterStatus
          if (status === 'Done') acc.done++
          else if (status === 'In Progress') acc.inProgress++
          else if (status === 'Needs Review') acc.needsReview++
          else if (status === 'Need More Info') acc.needMoreInfo++
          else if (status === 'On Hold') acc.onHold++
          else acc.notStarted++
          return acc
        },
        { ...defaultCounts }
      )

      const total = subtasks.length
      const progressSummary = { ...statusCounts, total }

      const chartData = [
        { label: 'done', value: statusCounts.done, color: 'rgb(80, 200, 120)' },
        { label: 'inProgress', value: statusCounts.inProgress, color: 'rgb(249, 167, 49)' },
        { label: 'needsReview', value: statusCounts.needsReview, color: 'rgb(33, 201, 223)' },
        { label: 'needMoreInfo', value: statusCounts.needMoreInfo, color: 'rgb(183, 91, 249)' },
        { label: 'notStarted', value: statusCounts.notStarted, color: 'rgb(119, 129, 129)' },
        { label: 'onHold', value: statusCounts.onHold, color: 'rgb(201, 92, 118)' },
      ]

      const completionRatio = total > 0 ? (statusCounts.done / total) * 100 : 0
      return { progressSummary, chartData, completionRatio }
    }

    // === 2) New path: derive from checklist/progress on the parent task ===
    const task = opts?.task
    if (task) {
      // prefer compact `progress` if present
      const hasProgress = task.progress && Number(task.progress.total) > 0
      const fromProgress = hasProgress
        ? {
            done: Number(task.progress.done) || 0,
            total: Number(task.progress.total) || 0,
          }
        : null

      // else compute from `checklist` array
      const list = Array.isArray(task.checklist) ? task.checklist : []
      const fromChecklist =
        !fromProgress && list.length > 0
          ? {
              done: list.filter(i => i?.done).length,
              total: list.length,
            }
          : null

      const basis = fromProgress || fromChecklist
      if (basis && basis.total > 0) {
        const done = basis.done
        const total = basis.total
        const notStarted = Math.max(0, total - done)

        const progressSummary = {
          done,
          inProgress: 0,
          onHold: 0,
          needsReview: 0,
          needMoreInfo: 0,
          notStarted,
          total,
        }

        const chartData = [
          { label: 'done', value: done, color: 'rgb(80, 200, 120)' },
          { label: 'notStarted', value: notStarted, color: 'rgb(119, 129, 129)' },
        ]

        const completionRatio = (done / total) * 100
        return { progressSummary, chartData, completionRatio }
      }
    }

    // === 3) Nothing available ===
    const progressSummary = { ...defaultCounts, total: 0 }
    const chartData = [
      { label: 'done', value: 0, color: 'rgb(80, 200, 120)' },
      { label: 'inProgress', value: 0, color: 'rgb(249, 167, 49)' },
      { label: 'needsReview', value: 0, color: 'rgb(33, 201, 223)' },
      { label: 'needMoreInfo', value: 0, color: 'rgb(183, 91, 249)' },
      { label: 'notStarted', value: 0, color: 'rgb(119, 129, 129)' },
      { label: 'onHold', value: 0, color: 'rgb(201, 92, 118)' },
    ]
    const completionRatio = 0
    return { progressSummary, chartData, completionRatio }
  }, [])
}
